/**
 * プロビジョニングジョブの進捗表示。
 *
 * 作成・削除・プラン変更などは `202 Accepted` + `{ jobId }` で返る（§4.3）。
 * 契約者に見せてよいのは `state` / `progress` / `userMessage` の 3 つだけで、
 * 内部のステップ名やエラー本文は `GET /portal/jobs/:jobId` が返さない。
 *
 * ポーリングは 1 秒から始めて 5 秒まで伸ばす（§5.3）。terminal に達したら止める。
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, StatusBadge, Button } from '@mail-sv/ui';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import type { JobProgress } from '../lib/api-types.js';

const TERMINAL = new Set(['succeeded', 'failed', 'canceled', 'compensated']);

export function isTerminal(state: string | undefined): boolean {
  return state !== undefined && TERMINAL.has(state);
}

/**
 * ジョブ 1 件を追跡する。`jobId` が null の間は何もしない。
 * 完了・失敗のたびに `onSettled` を 1 回だけ呼ぶ。
 */
export function useJobProgress(
  jobId: string | null,
  onSettled?: (job: JobProgress) => void,
): { job: JobProgress | undefined; isPending: boolean } {
  const settledRef = React.useRef<string | null>(null);

  const query = useQuery({
    queryKey: qk.job(jobId ?? 'none'),
    queryFn: () => api.get<JobProgress>(`/jobs/${encodeURIComponent(jobId!)}`),
    enabled: jobId !== null,
    // 進行中は 1s → 5s へ伸ばす。terminal で停止。
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      if (isTerminal(state)) return false;
      const n = query.state.dataUpdateCount;
      return Math.min(5000, 1000 + n * 1000);
    },
    // 進捗は常に最新が欲しいのでキャッシュを効かせない。
    staleTime: 0,
    gcTime: 60_000,
  });

  React.useEffect(() => {
    const job = query.data;
    if (!job || !isTerminal(job.state)) return;
    if (settledRef.current === job.jobId) return;
    settledRef.current = job.jobId;
    onSettled?.(job);
  }, [query.data, onSettled]);

  return { job: query.data, isPending: jobId !== null && !isTerminal(query.data?.state) };
}

/** 進捗バー + 一言。ダイアログの中にも一覧の上にも置ける。 */
export function JobProgressBar({ job }: { job: JobProgress | undefined }) {
  // `progress` は **0..100 の整数**（`provisioning_jobs.progress`）。
  // 0..1 として扱うと、途中経過が常に 0% か 100% にしか見えない。
  const ratio = Math.min(1, Math.max(0, (job?.progress ?? 0) / 100));
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <StatusBadge status={job?.state ?? 'queued'} dot pulse={job?.state === 'running'} />
        <span className="tabular text-xs text-muted-foreground">{Math.round(ratio * 100)}%</span>
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={Math.round(ratio * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`h-full rounded-full transition-all ${
            job?.state === 'failed' ? 'bg-destructive' : 'bg-primary'
          }`}
          style={{ width: `${ratio * 100}%` }}
        />
      </div>
      <p className="text-sm text-muted-foreground">
        {job?.userMessage ?? 'サーバーに設定を反映しています…'}
      </p>
    </div>
  );
}

/**
 * 反映待ちダイアログ。
 * 「メールアドレスを作ったのに、まだメールソフトでログインできない」という
 * 問い合わせを減らすため、実際に使えるようになるまで画面に留める。
 */
export function JobProgressDialog({
  open,
  onOpenChange,
  title,
  jobId,
  onDone,
  doneLabel = '閉じる',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  jobId: string | null;
  onDone?: (job: JobProgress) => void;
  doneLabel?: string;
}) {
  const { job } = useJobProgress(jobId, onDone);
  const finished = isTerminal(job?.state);
  /*
   * **続きが外部サイトにあるジョブの受け皿。**
   *
   * カード登録（`billing.payment-method-session`）のようなジョブは、
   * 決済事業者の画面へ遷移して初めて完了する。ワーカーは遷移先を
   * `provisioning_jobs.output.redirectUrl` に書き戻す設計なので、
   * 契約者面のジョブ参照がそれを返せば、ここで「開く」を出せる。
   *
   * **返ってこない環境でも壊れない形にしてある**（項目が無ければ
   * ボタンが出ないだけ）。自動遷移はしない — ダイアログが勝手に外部
   * サイトへ飛ぶと、押した覚えのない遷移になる。
   */
  const redirectUrl = job?.state === 'succeeded' ? (job.redirectUrl ?? null) : null;
  const expired =
    job?.redirectExpiresAt != null && new Date(job.redirectExpiresAt).getTime() < Date.now();

  return (
    <Dialog open={open} onOpenChange={(next) => (finished ? onOpenChange(next) : undefined)}>
      <DialogContent className="max-w-md" hideClose={!finished}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <JobProgressBar job={job} />
        <p className="text-xs text-muted-foreground">
          サーバーへの反映は通常 1 分以内に完了します。この画面を閉じても処理は続きます。
        </p>
        {redirectUrl && !expired ? (
          <p className="text-xs text-muted-foreground">
            続きの手続きは、この先のページで行います。
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {finished ? doneLabel : '閉じる（処理は続きます）'}
          </Button>
          {redirectUrl && !expired ? (
            <Button type="button" onClick={() => window.location.assign(redirectUrl)}>
              手続きのページを開く
            </Button>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
